import { DatabaseRow } from "@Shape-Digital/kudzu-data/lib/types/common";
import { SupabaseClient } from "@supabase/supabase-js";
import endOfDay from "date-fns/endOfDay";
import startOfDay from "date-fns/startOfDay";
import subDays from "date-fns/subDays";
import { formatClientTimezoneToUTC_ISO } from "../../../common/dateHelpers";

type CenterOffering = Pick<
  DatabaseRow<"center_offerings">,
  "name" | "duration_minutes"
>;

type AppointmentCenter = Pick<
  DatabaseRow<"centers">,
  "name" | "region" | "timezone" | "city"
> & {
  center_configs: Pick<
    DatabaseRow<"center_configs">,
    | "priority_report_deadline_hours"
    | "report_delivery_deadline_hours"
    | "stat_report_deadline_hours"
  >[];
};

type AppointmentTimeSlot = Pick<DatabaseRow<"center_time_slots">, "started_at">;

type Patient = Pick<DatabaseRow<"patients">, "mrn">;

type AppointmentPatientDetails = Pick<
  DatabaseRow<"appointment_patient_details">,
  "first_name" | "last_name" | "date_birth" | "mobile_phone_number"
> & {
  patient: Patient;
};

type AppointmentAIReport = Pick<
  DatabaseRow<"appointment_ai_reports">,
  | "id"
  | "status"
  | "created_at"
  | "summary_section"
  | "findings_section"
  | "next_steps_section"
>;

type Appointment = DatabaseRow<"appointments"> & {
  appointment_patient_details: AppointmentPatientDetails[];
  time_slot: AppointmentTimeSlot;
  center: AppointmentCenter;
  appointment_ai_reports: AppointmentAIReport[];
  appointment_offerings: {
    side: string | null;
    center_offering: CenterOffering;
  }[];
  is_stat: boolean;
  has_priority: boolean;
  is_additional_views: boolean;
  has_previous_appointments: boolean;
  is_contacting_patient_required: boolean;
  ready_to_read_at: string | null;
};

export type DashboardAppointmentAIReport = AppointmentAIReport & {
  appointment: Appointment;
};

const supabaseAppointmentAIReportsRequest = `
id,
status,
created_at,
summary_section,
findings_section,
next_steps_section,
appointment:appointment_id!inner (
  id,
  status,
  is_stat,
  is_contacting_patient_required,
  has_priority,
  has_previous_appointments,
  is_additional_views,
  ready_to_read_at,
  appointment_patient_details (
    first_name,
    last_name,
    date_birth,
    mobile_phone_number,
    patient:patient_id (
      mrn
    )
  ),
  time_slot:time_slot_id!inner (
    started_at
  ),
  center:center_id!inner (
    name,
    region,
    timezone,
    city,
    center_configs (
      priority_report_deadline_hours,
      report_delivery_deadline_hours,
      stat_report_deadline_hours
    )
  ),
  appointment_offerings (
    side,
    center_offering: center_offering_id (
      name,
      duration_minutes
    )
  )
)
`;

const today = new Date();

const getAppointmentAIReports = async (params: {
  supabase: SupabaseClient;
  centerIds: string[];
  isApprovedShown: boolean;
  showApprovedReportsDays: number;
  timezone?: string | null;
}) => {
  const {
    supabase,
    centerIds,
    isApprovedShown,
    showApprovedReportsDays,
    timezone,
  } = params;

  const startOfDate = formatClientTimezoneToUTC_ISO(
    subDays(startOfDay(today), showApprovedReportsDays),
    timezone,
  );
  const endOfDate = formatClientTimezoneToUTC_ISO(endOfDay(today), timezone);

  const dateFilter = `and(started_at.gte.${startOfDate},started_at.lte.${endOfDate})`;

  const statusFilter = isApprovedShown
    ? ["unsent", "sent"]
    : ["requires_review", "in_review"];

  let query = supabase
    .from("appointment_ai_reports")
    .select(supabaseAppointmentAIReportsRequest)
    .order("id")
    .not(
      "appointment.appointment_patient_details.patient_id" as keyof Appointment,
      "is",
      null,
    )
    .in("status", statusFilter);

  if (isApprovedShown) {
    query = query.or(dateFilter, { foreignTable: "appointment.time_slot" });
  }

  if (centerIds.length) {
    query = query.in(
      "appointment.center_id" as keyof DashboardAppointmentAIReport,
      centerIds,
    );
  }

  const { data, error } = await query
    .limit(1, { foreignTable: "appointment.center.center_configs" })
    .returns<DashboardAppointmentAIReport[]>();

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

export default getAppointmentAIReports;
